import React, {useContext} from 'react';
import {Avatar, Box, ListItem, ListItemAvatar, ListItemText,} from '@mui/material';
import {ArrowForwardIos, Folder} from '@mui/icons-material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {restore_slashes} from '../background_functions';
import ContextProviderElementFunctions from '../Contexts/ContectProviderElementFunctions';
import {ShowPDF} from '../ElementFunctions/ShowPDF/ShowPDF';
import {DownloadFileFunction} from '../ElementFunctions/DownloadFunctions/DownloadFileFunction';
import {EditSharedUsersAndUsergroups} from '../ElementFunctions/EditSharedUsersAndUsergroups/EditSharedUsersAndUsergroups';
import {UploadFile} from '../ElementFunctions/UploadFile/UploadFile';
import {AddFolder} from '../ElementFunctions/AddFolder/AddFolder';
import {DeleteFile} from '../ElementFunctions/DeleteFile/DeleteFile';
import {MoveFile} from '../ElementFunctions/MoveFile/MoveFile';
import {downloadFile} from '../ElementFunctions/DownloadFunctions/background_tasks';
import {UserAvatarList} from './UserAvatarList';
import {useDraggable, useDroppable} from '@dnd-kit/core';
import {DocumentsService} from '../../services/openapi';
import APIWrapperContext from '../../services/APIWrapperContext';
import {FileWithPath} from 'react-dropzone';

interface FileListItemProps {
    element: any;
    accessibleElements: Record<string, any>;
    userSpecific: boolean | undefined;
    onDirectoryClick: (id: string) => void;
    setLoading: (loading: boolean) => void;
    onDeleteStart?: () => void;
    onDeleteSuccess?: () => void;
    onDeleteError?: () => void;
    onMoveStart?: () => void;
    onMoveSuccess?: () => void;
    onMoveError?: () => void;
    onUploadStart?: (files: FileWithPath[]) => void;
    onUploadSuccess?: (files: FileWithPath[]) => void;
    onUploadError?: (files: FileWithPath[]) => void;
}

export const FileListItem: React.FC<FileListItemProps> = ({
                                                              element: el,
                                                              accessibleElements,
                                                              userSpecific,
                                                              onDirectoryClick,
                                                              setLoading,
                                                              onDeleteStart,
                                                              onDeleteSuccess,
                                                              onDeleteError,
                                                              onMoveStart,
                                                              onMoveSuccess,
                                                              onMoveError,
                                                              onUploadStart,
                                                              onUploadSuccess,
                                                              onUploadError,
                                                          }) => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const [isUnread, setIsUnread] = React.useState(el.isUnread);

    const markAsRead = () => {
        if (isUnread) {
            call_backend_api(
                DocumentsService.markDocumentAsReadDocMarkAsReadDocumentHidriveIdPost(el.hidrive_id),
                () => {
                    el.isUnread = false;
                    setIsUnread(false);
                },
                undefined,
                undefined,
                false
            );
        }
    };

    const handleItemClick = () => {
        setLoading(true);
        if (el.type === 'dir') {
            onDirectoryClick(el.hidrive_id);
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        } else if (el.type === 'system') {
            setLoading(false);
        } else {
            markAsRead();
            setLoading(false);
        }
    };

    const {
        attributes,
        listeners,
        setNodeRef: setDraggableNodeRef,
        transform,
    } = useDraggable({
        id: el.hidrive_id,
        data: el,
        disabled: true,
    });

    const {setNodeRef: setDroppableNodeRef, isOver} = useDroppable({
        id: el.hidrive_id,
        data: el,
    });

    const style = transform
        ? {
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
            zIndex: 9999,
            opacity: 0.7,
        }
        : undefined;

    const combinedRef = (element: HTMLLIElement) => {
        setDroppableNodeRef(element);
        setDraggableNodeRef(element);
    };

    // Get the filename/foldername from parent path
    const fileName = restore_slashes(el.parent);

    return (
        <ListItem
            id={el.hidrive_id}
            sx={{
                ':hover': {backgroundColor: '#f0f0f0', cursor: 'pointer'},
                minHeight: '64px',
                display: 'flex',
                alignItems: 'center',
                padding: '8px',
            }}
            onClick={handleItemClick}
            ref={combinedRef}
            style={style}
            {...listeners}
            {...attributes}>
            <ListItemAvatar sx={{position: 'relative'}}>
                <Avatar>{el.type === 'file' ? <InsertDriveFileIcon color='primary'/> : <Folder/>}</Avatar>
                {isUnread && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 2,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: 'red',
                            animation: 'pulse 2.5s infinite',
                            '@keyframes pulse': {
                                '0%': {
                                    transform: 'scale(1)',
                                    opacity: 1,
                                },
                                '50%': {
                                    transform: 'scale(1.4)',
                                    opacity: 0.7,
                                },
                                '100%': {
                                    transform: 'scale(1)',
                                    opacity: 1,
                                },
                            },
                        }}
                    />
                )}
            </ListItemAvatar>

            <div
                style={{
                    flexGrow: 1,
                    overflow: 'hidden',
                }}>
                <ListItemText
                    onClick={(e) => {
                        if (el.type === 'file') {
                            markAsRead();
                            downloadFile(el.hidrive_id, el.parent);
                        }
                    }}
                    primary={fileName}
                    secondary=''
                    primaryTypographyProps={{
                        style: {
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                        },
                    }}
                />
            </div>

            <ListItemAvatar>
                <UserAvatarList accessibleElements={accessibleElements} elementId={el.hidrive_id}/>
            </ListItemAvatar>
            {el.type !== 'system' && (
                <ContextProviderElementFunctions
                    key={'ContextProviderElementFunctions_' + el.hidrive_id}
                    curr_dir_id={el.hidrive_id}
                    type={el.type}
                    name={el.parent}>
                    <ShowPDF onViewStart={markAsRead}/>
                    {el.type !== 'dir' ? <DownloadFileFunction onDownloadStart={markAsRead}/> : <></>}
                    {el.type === 'dir' && <ArrowForwardIos/>}
                    {(userSpecific === undefined || userSpecific === false) && (
                        <>
                            <EditSharedUsersAndUsergroups/>
                            <UploadFile
                                onUploadStart={onUploadStart}
                                onUploadSuccess={onUploadSuccess}
                                onUploadError={onUploadError}
                            />
                            <AddFolder/>
              {el.type === 'file' ? (
                  <>
                      {!userSpecific && (
                          <MoveFile
                              curr_dir_id={el.hidrive_id}
                              type={el.type}
                              name={el.parent}
                              onMoveStart={onMoveStart}
                              onMoveSuccess={onMoveSuccess}
                              onMoveError={onMoveError}
                          />
                      )}
                      <DeleteFile
                          curr_dir_id={el.hidrive_id}
                          type={el.type}
                          name={el.parent}
                          onDeleteStart={onDeleteStart}
                          onDeleteSuccess={onDeleteSuccess}
                          onDeleteError={onDeleteError}
                      />
                  </>
              ) : (
                  <></>
              )}
                        </>
                    )}
                </ContextProviderElementFunctions>
            )}
        </ListItem>
    );
};
